import React, { useState } from 'react';
import { X, AlertTriangle, CheckCircle2, FileText, Clock, Settings, ExternalLink, Mail, ChevronUp, ChevronDown, Plus, GripVertical, Search } from 'lucide-react';
import { Loan, ModalType, Role, ColumnScope, ALL_COLUMN_SCOPES, COLUMN_LABELS, COLUMN_DESCRIPTIONS, ColumnKey, daysUntilLockExp, isLockExpiredOrToday, TODAY } from '../data';
interface ModalWrapperProps {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  accent?: 'sos' | 'cobalt' | 'amber';
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: string;
}
function ModalWrapper({
  title,
  subtitle,
  icon,
  accent = 'cobalt',
  onClose,
  children,
  maxWidth = 'max-w-4xl'
}: ModalWrapperProps) {
  const accentBg = accent === 'sos' ? 'bg-sos-50 dark:bg-sos-900/20 border-b-sos-200/60 dark:border-b-sos-900/40' : accent === 'amber' ? 'bg-amber-50 dark:bg-amber-900/20 border-b-amber-200/60 dark:border-b-amber-900/40' : 'bg-cobalt-50 dark:bg-cobalt-900/20 border-b-cobalt-200/60 dark:border-b-cobalt-900/40';
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-id="element-161">
      <div className={`bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full ${maxWidth} flex flex-col max-h-[90vh] overflow-hidden border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-200`} data-id="element-162">
        <div className={`flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 ${accentBg}`} data-id="element-163">
          <div className="flex items-center space-x-3" data-id="element-164">
            {icon}
            <div data-id="element-165">
              <h2 className="text-base font-semibold text-slate-900 dark:text-white leading-tight" data-id="element-166">
                {title}
              </h2>
              {subtitle && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5" data-id="element-167">
                  {subtitle}
                </p>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-full hover:bg-white/60 dark:hover:bg-slate-800 transition-colors" data-id="element-168">
            <X className="w-5 h-5" data-id="element-169" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto" data-id="element-170">{children}</div>
      </div>
    </div>;
}
const LoanNameLink = ({
  name
}: {
  name: string;
}) => <a href="#" target="_blank" rel="noopener noreferrer" className="inline-flex items-center font-medium text-cobalt-600 dark:text-cobalt-400 hover:underline" data-id="element-171">
    {name}
    <ExternalLink className="w-3 h-3 ml-1" data-id="element-172" />
  </a>;
interface ModalsProps {
  activeModal: ModalType;
  onClose: () => void;
  loans: Loan[];
  role: Role;
  onRequestClearSos: (loan: Loan) => void;
  alwaysIncludedRecipients: string[];
  setAlwaysIncludedRecipients: (next: string[]) => void;
  columnVisibility: Record<ColumnScope, ColumnKey[]>;
  setColumnVisibility: (next: Record<ColumnScope, ColumnKey[]>) => void;
  daysInPipeThresholds: {
    good: number;
    warning: number;
  };
  setDaysInPipeThresholds: (next: {
    good: number;
    warning: number;
  }) => void;
}
export function Modals({
  activeModal,
  onClose,
  loans,
  role,
  onRequestClearSos,
  alwaysIncludedRecipients,
  setAlwaysIncludedRecipients,
  columnVisibility,
  setColumnVisibility,
  daysInPipeThresholds,
  setDaysInPipeThresholds
}: ModalsProps) {
  if (!activeModal) return null;
  // SOS list = explicitly flagged + locks expiring today or earlier (auto follow expired-lock logic)
  const sosLoans = loans.filter(l => l.isSos || isLockExpiredOrToday(l.lockExpiration));
  const newAppLoans = loans.filter(l => l.isNewApp);
  const rateLockLoans = loans.filter(l => {
    const d = daysUntilLockExp(l.lockExpiration);
    return d <= 15 && d >= 0;
  });
  if (activeModal === 'sos') {
    return <ModalWrapper title="Active SOS Alerts" subtitle="Flagged loans and locks expiring today" icon={<AlertTriangle className="w-5 h-5 text-sos-500" data-id="element-174" />} accent="sos" onClose={onClose} data-id="element-173">
        <div className="space-y-3" data-id="element-175">
          {sosLoans.length === 0 ? <div className="text-center py-8 text-slate-500 dark:text-slate-400" data-id="element-176">
              No active SOS alerts.
            </div> : sosLoans.map(loan => {
          const isLockSos = !loan.isSos && isLockExpiredOrToday(loan.lockExpiration);
          return <div key={loan.id} className="border border-sos-200 dark:border-sos-900/50 rounded-lg p-4 bg-sos-50/30 dark:bg-sos-900/10" data-id="element-177">
                  <div className="flex justify-between items-start mb-3 gap-4" data-id="element-178">
                    <div className="min-w-0" data-id="element-179">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-180">
                        <h3 className="font-semibold text-lg" data-id="element-181">
                          <LoanNameLink name={loan.loanName} data-id="element-182" />
                        </h3>
                        {isLockSos && <span className="inline-flex items-center text-[10px] uppercase font-semibold tracking-wider bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded" data-id="element-183">
                            Lock Expired
                          </span>}
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400 mt-1" data-id="element-184">
                        Owner: {loan.loanOwner} · Processor: {loan.processor}
                      </div>
                      {loan.sosNote && <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5" data-id="element-185">
                          Opened{' '}
                          {new Date(loan.sosNote.timestamp).toLocaleString()}
                        </div>}
                    </div>
                    {loan.isSos && (role === 'Admin' || role === 'Processor') && <button onClick={() => onRequestClearSos(loan)} className="flex items-center space-x-1 px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm flex-shrink-0" data-id="element-186">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-187" />
                          <span data-id="element-188">Clear SOS</span>
                        </button>}
                  </div>
                  {loan.sosNoteHistory && loan.sosNoteHistory.length > 0 && <div className="space-y-1.5" data-id="element-189">
                      {[...loan.sosNoteHistory].reverse().map(n => {
                const ts = new Date(n.timestamp);
                const dateStr = String(ts.getMonth() + 1).padStart(2, '0') + '/' + String(ts.getDate()).padStart(2, '0') + '/' + ts.getFullYear();
                const timeStr = ts.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                });
                return <div key={n.id} className={`bg-white dark:bg-slate-800 px-3 py-2 rounded border text-sm ${n.kind === 'clear' ? 'border-emerald-200 dark:border-emerald-900/40' : 'border-slate-200 dark:border-slate-700'}`} data-id="element-190">
                            <div className="text-[11px] font-mono text-slate-500 dark:text-slate-400 mb-0.5" data-id="element-191">
                              [{dateStr} {timeStr} – {n.author}]
                              {n.kind === 'clear' && <span className="ml-1 inline-flex items-center text-[10px] font-semibold tracking-wider text-emerald-600 dark:text-emerald-400 uppercase" data-id="element-192">
                                  Cleared
                                </span>}
                            </div>
                            <div className="text-slate-800 dark:text-slate-200" data-id="element-193">
                              {n.text}
                            </div>
                          </div>;
              })}
                    </div>}
                </div>;
        })}
        </div>
      </ModalWrapper>;
  }
  if (activeModal === 'newApp') {
    return <ModalWrapper title="New Applications" subtitle="Recently received apps awaiting initial review" icon={<FileText className="w-5 h-5 text-cobalt-500" data-id="element-195" />} accent="cobalt" onClose={onClose} data-id="element-194">
        <table className="w-full text-sm text-left" data-id="element-196">
          <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-800/50" data-id="element-197">
            <tr data-id="element-198">
              <th className="px-4 py-3 rounded-tl-lg" data-id="element-199">Processor Received</th>
              <th className="px-4 py-3" data-id="element-200">Loan Name</th>
              <th className="px-4 py-3" data-id="element-201">Loan Owner</th>
              <th className="px-4 py-3 rounded-tr-lg" data-id="element-202">Processor Assistant</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800" data-id="element-203">
            {newAppLoans.map(loan => <tr key={loan.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-id="element-204">
                <td className="px-4 py-3 text-slate-600 dark:text-slate-300" data-id="element-205">
                  {loan.processor}
                </td>
                <td className="px-4 py-3" data-id="element-206">
                  <LoanNameLink name={loan.loanName} data-id="element-207" />
                </td>
                <td className="px-4 py-3 text-slate-600 dark:text-slate-300" data-id="element-208">
                  {loan.loanOwner}
                </td>
                <td className="px-4 py-3 text-slate-600 dark:text-slate-300" data-id="element-209">
                  {loan.processorAssistant}
                </td>
              </tr>)}
          </tbody>
        </table>
      </ModalWrapper>;
  }
  if (activeModal === 'rateLock') {
    return <ModalWrapper title="Locks Expiring Soon" subtitle="Locks expiring within 15 days" icon={<Clock className="w-5 h-5 text-amber-500" data-id="element-211" />} accent="amber" onClose={onClose} data-id="element-210">
        <table className="w-full text-sm text-left" data-id="element-212">
          <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-800/50" data-id="element-213">
            <tr data-id="element-214">
              <th className="px-4 py-3 rounded-tl-lg" data-id="element-215">Loan Name</th>
              <th className="px-4 py-3" data-id="element-216">Expiration Date</th>
              <th className="px-4 py-3" data-id="element-217">Days Left</th>
              <th className="px-4 py-3" data-id="element-218">Processor</th>
              <th className="px-4 py-3 rounded-tr-lg" data-id="element-219">Stage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800" data-id="element-220">
            {rateLockLoans.map(loan => {
            const d = daysUntilLockExp(loan.lockExpiration);
            return <tr key={loan.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-id="element-221">
                  <td className="px-4 py-3" data-id="element-222">
                    <LoanNameLink name={loan.loanName} data-id="element-223" />
                  </td>
                  <td className="px-4 py-3 text-amber-600 dark:text-amber-400 font-medium" data-id="element-224">
                    {loan.lockExpiration}
                  </td>
                  <td className="px-4 py-3" data-id="element-225">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${d <= 3 ? 'bg-sos-100 text-sos-700 dark:bg-sos-900/40 dark:text-sos-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`} data-id="element-226">
                      {d === 0 ? 'Today' : `${d}d`}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300" data-id="element-227">
                    {loan.processor}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300" data-id="element-228">
                    {loan.stage}
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </ModalWrapper>;
  }
  if (activeModal === 'admin') {
    return <AdminDrawer onClose={onClose} alwaysIncludedRecipients={alwaysIncludedRecipients} setAlwaysIncludedRecipients={setAlwaysIncludedRecipients} columnVisibility={columnVisibility} setColumnVisibility={setColumnVisibility} daysInPipeThresholds={daysInPipeThresholds} setDaysInPipeThresholds={setDaysInPipeThresholds} data-id="element-229" />;
  }
  return null;
}
// ---------- Admin Drawer ----------
interface AdminDrawerProps {
  onClose: () => void;
  alwaysIncludedRecipients: string[];
  setAlwaysIncludedRecipients: (next: string[]) => void;
  columnVisibility: Record<ColumnScope, ColumnKey[]>;
  setColumnVisibility: (next: Record<ColumnScope, ColumnKey[]>) => void;
  daysInPipeThresholds: {
    good: number;
    warning: number;
  };
  setDaysInPipeThresholds: (next: {
    good: number;
    warning: number;
  }) => void;
}
const ALL_COLUMN_KEYS: ColumnKey[] = ['sos', 'daysInPipe', 'monthPushing', 'loanName', 'stage', 'processorNote', 'loanOwner', 'teamLead', 'processor', 'processorAssistant', 'loanAmount', 'id', 'lockExpiration', 'scheduledClosing', 'scheduledFunding', 'isNewApp', 'sosRecipients'];
function AdminDrawer({
  onClose,
  alwaysIncludedRecipients,
  setAlwaysIncludedRecipients,
  columnVisibility,
  setColumnVisibility,
  daysInPipeThresholds,
  setDaysInPipeThresholds
}: AdminDrawerProps) {
  const [activeRoleTab, setActiveRoleTab] = useState<ColumnScope>('Processor');
  const [emailInput, setEmailInput] = useState('');
  const [columnSearch, setColumnSearch] = useState('');
  const setRoleColumns = (scope: ColumnScope, next: ColumnKey[]) => setColumnVisibility({
    ...columnVisibility,
    [scope]: next
  });
  const removeColumn = (scope: ColumnScope, col: ColumnKey) => {
    if (col === 'sos') return; // locked
    setRoleColumns(scope, columnVisibility[scope].filter(c => c !== col));
  };
  const addColumn = (scope: ColumnScope, col: ColumnKey) => {
    if (columnVisibility[scope].includes(col)) return;
    setRoleColumns(scope, [...columnVisibility[scope], col]);
  };
  const moveColumn = (scope: ColumnScope, col: ColumnKey, dir: -1 | 1) => {
    const cols = [...columnVisibility[scope]];
    const idx = cols.indexOf(col);
    if (idx === -1) return;
    const target = idx + dir;
    // SOS is locked at position 0 — don't allow swapping into it
    if (target < 1 || target >= cols.length) return;
    [cols[idx], cols[target]] = [cols[target], cols[idx]];
    setRoleColumns(scope, cols);
  };
  const addEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    if (alwaysIncludedRecipients.includes(trimmed)) return;
    setAlwaysIncludedRecipients([...alwaysIncludedRecipients, trimmed]);
    setEmailInput('');
  };
  const removeEmail = (e: string) => setAlwaysIncludedRecipients(alwaysIncludedRecipients.filter(x => x !== e));
  return <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30 backdrop-blur-sm" data-id="element-230">
      <div className="bg-white dark:bg-slate-900 w-full max-w-lg h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-200 dark:border-slate-800" data-id="element-231">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200 dark:border-slate-800" data-id="element-232">
          <div className="flex items-center space-x-3" data-id="element-233">
            <Settings className="w-5 h-5 text-slate-500" data-id="element-234" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white" data-id="element-235">
              Console Configuration
            </h2>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-id="element-236">
            <X className="w-5 h-5" data-id="element-237" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-238">
          <section data-id="element-239">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-3" data-id="element-240">
              Alert Thresholds
            </h3>

            <div className="mb-4" data-id="element-241">
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1" data-id="element-242">
                Rate Lock Warning (Days)
              </label>
              <input type="number" defaultValue={15} className="w-32 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-cobalt-500 outline-none dark:text-white" data-id="element-243" />
            </div>

            <div className="border-t border-slate-200 dark:border-slate-700 pt-4" data-id="element-244">
              <div className="flex items-center justify-between mb-2" data-id="element-245">
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400" data-id="element-246">
                  Days In Pipe Color Bands
                </label>
                <button onClick={() => setDaysInPipeThresholds({
                good: 15,
                warning: 30
              })} className="text-[11px] text-cobalt-600 dark:text-cobalt-400 hover:underline" data-id="element-247">
                  Reset defaults
                </button>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3" data-id="element-248">
                Set the day counts that drive the color of the Days In Pipe
                badge.
              </p>

              <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2 mb-3" data-id="element-249">
                <div data-id="element-250">
                  <label className="block text-[11px] font-medium text-emerald-700 dark:text-emerald-400 mb-1" data-id="element-251">
                    Green &lt;
                  </label>
                  <input type="number" min={1} value={daysInPipeThresholds.good} onChange={e => {
                  const good = Math.max(1, Number(e.target.value) || 1);
                  setDaysInPipeThresholds({
                    good,
                    warning: Math.max(good, daysInPipeThresholds.warning)
                  });
                }} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white" data-id="element-252" />
                </div>
                <div className="text-xs text-slate-400 dark:text-slate-500 pb-2.5 px-1" data-id="element-253">
                  Amber ≤
                </div>
                <div data-id="element-254">
                  <label className="block text-[11px] font-medium text-amber-700 dark:text-amber-400 mb-1" data-id="element-255">
                    Red &gt;
                  </label>
                  <input type="number" min={daysInPipeThresholds.good} value={daysInPipeThresholds.warning} onChange={e => {
                  const warning = Math.max(daysInPipeThresholds.good, Number(e.target.value) || daysInPipeThresholds.good);
                  setDaysInPipeThresholds({
                    good: daysInPipeThresholds.good,
                    warning
                  });
                }} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 outline-none dark:text-white" data-id="element-256" />
                </div>
              </div>

              <div className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400" data-id="element-257">
                <span data-id="element-258">Preview:</span>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400" data-id="element-259">
                  {Math.max(0, daysInPipeThresholds.good - 1)} days
                </span>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400" data-id="element-260">
                  {daysInPipeThresholds.warning} days
                </span>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" data-id="element-261">
                  {daysInPipeThresholds.warning + 5} days
                </span>
              </div>
            </div>
          </section>

          <section data-id="element-262">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-3 flex items-center" data-id="element-263">
              <Mail className="w-4 h-4 mr-2 text-slate-400" data-id="element-264" />
              SOS Always-Included Recipients
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3" data-id="element-265">
              These emails are CC'd on every new SOS alert in addition to those
              chosen by the user.
            </p>
            <div className="flex flex-wrap gap-1.5 mb-2" data-id="element-266">
              {alwaysIncludedRecipients.length === 0 && <span className="text-xs italic text-slate-400" data-id="element-267">
                  No recipients added.
                </span>}
              {alwaysIncludedRecipients.map(e => <span key={e} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-cobalt-100 text-cobalt-700 dark:bg-cobalt-900/40 dark:text-cobalt-300" data-id="element-268">
                  {e}
                  <button onClick={() => removeEmail(e)} className="ml-1 hover:text-sos-500" data-id="element-269">
                    <X className="w-3 h-3" data-id="element-270" />
                  </button>
                </span>)}
            </div>
            <div className="flex gap-2" data-id="element-271">
              <input value={emailInput} onChange={e => setEmailInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addEmail()} placeholder="management@activelink.com" className="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-cobalt-500 outline-none dark:text-white" data-id="element-272" />
              <button onClick={addEmail} className="px-3 py-2 bg-cobalt-600 hover:bg-cobalt-700 text-white text-sm font-medium rounded-md shadow-sm" data-id="element-273">
                Add
              </button>
            </div>
          </section>

          <section data-id="element-274">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider mb-1" data-id="element-275">
              Columns by View
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3" data-id="element-276">
              Reorder visible columns with the arrows. SOS is locked first.
            </p>
            <div className="flex border-b border-slate-200 dark:border-slate-700 mb-3 overflow-x-auto" data-id="element-277">
              {ALL_COLUMN_SCOPES.map(r => <button key={r} onClick={() => setActiveRoleTab(r)} className={`px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${activeRoleTab === r ? 'border-cobalt-600 text-cobalt-600 dark:text-cobalt-400' : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`} data-id="element-278">
                  {r}
                </button>)}
            </div>

            {/* Active columns — ordered */}
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5" data-id="element-279">
              Active ({columnVisibility[activeRoleTab].length})
            </div>
            <ul className="border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden divide-y divide-slate-100 dark:divide-slate-800 mb-4" data-id="element-280">
              {columnVisibility[activeRoleTab].map((col, idx) => {
              const isSos = col === 'sos';
              const isFirst = idx === 0;
              const isLast = idx === columnVisibility[activeRoleTab].length - 1;
              return <li key={col} className="flex items-center justify-between px-2 py-1.5 bg-white dark:bg-slate-800/40 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" data-id="element-281">
                    <div className="flex items-center gap-2 min-w-0" data-id="element-282">
                      <GripVertical className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 flex-shrink-0" data-id="element-283" />
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-slate-100 dark:bg-slate-700 text-[10px] font-semibold text-slate-500 dark:text-slate-400 flex-shrink-0" data-id="element-284">
                        {idx + 1}
                      </span>
                      <span className="text-sm text-slate-700 dark:text-slate-200 truncate" data-id="element-285">
                        {COLUMN_LABELS[col]}
                      </span>
                      {isSos && <span className="text-[10px] uppercase font-semibold tracking-wider text-slate-400 dark:text-slate-500" data-id="element-286">
                          locked
                        </span>}
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0" data-id="element-287">
                      <button onClick={() => moveColumn(activeRoleTab, col, -1)} disabled={isSos || isFirst || idx === 1} aria-label="Move up" className="p-1 rounded text-slate-400 hover:text-cobalt-600 hover:bg-cobalt-50 dark:hover:text-cobalt-400 dark:hover:bg-cobalt-900/30 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors" data-id="element-288">
                        <ChevronUp className="w-3.5 h-3.5" data-id="element-289" />
                      </button>
                      <button onClick={() => moveColumn(activeRoleTab, col, 1)} disabled={isSos || isLast} aria-label="Move down" className="p-1 rounded text-slate-400 hover:text-cobalt-600 hover:bg-cobalt-50 dark:hover:text-cobalt-400 dark:hover:bg-cobalt-900/30 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors" data-id="element-290">
                        <ChevronDown className="w-3.5 h-3.5" data-id="element-291" />
                      </button>
                      <button onClick={() => removeColumn(activeRoleTab, col)} disabled={isSos} aria-label="Hide column" className="p-1 ml-0.5 rounded text-slate-400 hover:text-sos-500 hover:bg-sos-50 dark:hover:text-sos-400 dark:hover:bg-sos-900/30 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors" data-id="element-292">
                        <X className="w-3.5 h-3.5" data-id="element-293" />
                      </button>
                    </div>
                  </li>;
            })}
            </ul>

            {/* Add column — search-driven picker over all available Loan fields */}
            {(() => {
            const hidden = ALL_COLUMN_KEYS.filter(c => !columnVisibility[activeRoleTab].includes(c));
            const q = columnSearch.trim().toLowerCase();
            const matches = q ? hidden.filter(c => {
              const label = COLUMN_LABELS[c].toLowerCase();
              const desc = (COLUMN_DESCRIPTIONS[c] || '').toLowerCase();
              const key = c.toLowerCase();
              return label.includes(q) || desc.includes(q) || key.includes(q);
            }) : hidden;
            return <div data-id="element-294">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5" data-id="element-295">
                    Add a column
                  </div>
                  <div className="relative mb-2" data-id="element-296">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" data-id="element-297" />
                    <input type="text" value={columnSearch} onChange={e => setColumnSearch(e.target.value)} placeholder={hidden.length === 0 ? 'All loan fields are already in use' : 'Search loan fields (e.g. lock, funding, ID)…'} disabled={hidden.length === 0} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md pl-8 pr-8 py-2 text-sm focus:ring-2 focus:ring-cobalt-500 outline-none dark:text-white disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-298" />
                    {columnSearch && <button onClick={() => setColumnSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700" aria-label="Clear search" data-id="element-299">
                        <X className="w-3 h-3" data-id="element-300" />
                      </button>}
                  </div>

                  {hidden.length > 0 && <div className="border border-slate-200 dark:border-slate-700 rounded-md max-h-48 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800" data-id="element-301">
                      {matches.length === 0 ? <div className="px-3 py-3 text-xs text-slate-500 dark:text-slate-400 text-center italic" data-id="element-302">
                          No fields match "{columnSearch}"
                        </div> : matches.map(col => <button key={col} onClick={() => {
                  addColumn(activeRoleTab, col);
                  setColumnSearch('');
                }} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-cobalt-50 dark:hover:bg-cobalt-900/20 transition-colors group" data-id="element-303">
                            <Plus className="w-3.5 h-3.5 text-slate-400 group-hover:text-cobalt-600 dark:group-hover:text-cobalt-400 flex-shrink-0" data-id="element-304" />
                            <div className="min-w-0 flex-1" data-id="element-305">
                              <div className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate" data-id="element-306">
                                {COLUMN_LABELS[col]}
                              </div>
                              {COLUMN_DESCRIPTIONS[col] && <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate" data-id="element-307">
                                  {COLUMN_DESCRIPTIONS[col]}
                                </div>}
                            </div>
                            <code className="text-[10px] font-mono text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-700/60 px-1.5 py-0.5 rounded flex-shrink-0" data-id="element-308">
                              {col}
                            </code>
                          </button>)}
                    </div>}
                </div>;
          })()}
          </section>
        </div>

        <div className="p-6 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50" data-id="element-309">
          <button onClick={onClose} className="w-full bg-cobalt-600 hover:bg-cobalt-700 text-white font-medium py-2.5 rounded-lg transition-colors shadow-sm" data-id="element-310">
            Save Configuration
          </button>
        </div>
      </div>
    </div>;
}